import { IkeFieldType, IkeCloneable, IkeCollectorType } from '@ikegps/core-api';
import {
	FormModel,
	FormModelFieldNestedList,
	PoleFieldIds,
	FormModelFieldNestedListBuilderGroup,
	FormModelFieldNestedListBuilderValue,
	FormModelFieldSubForm,
	FormModelFieldSelectList,
	SpanType,
	SPAN_ENVIRONMENT_VALUES,
} from '..';
import { normalClasses, hClasses, PoleClass, poleLengthsForClass } from './AnsiO5.1/PoleDimensions';
import { BaseForm } from './BaseForm';
import { IkeUnit } from '../../unit';

export class BaseFormFactory {
	constructor(
		public form: FormModel,
		private hasVerticalSeparationToken = false
	) {}

	public get baseForm(): BaseForm {
		return new BaseForm(this.form);
	}

	public rootPoleSubForm() {
		this.form.Mutator.poleType();
	}

	public addBaseFormFields() {
		this.addStatusField();
		this.addSequenceField();
		this.addIdField();
		this.addTagPhotosField();
		this.addTypeField();
		this.addOwnerField(this.form, PoleFieldIds.POLE_OWNER);
		this.addCircumferenceField();
		this.addTipField();
		this.addLowPowerAttachmentField();
		this.addLocationField();
		this.addStreetAddressField();
		this.addNoteField(this.form, PoleFieldIds.NOTE);
		this.addIkePhotoField();
		this.addLowPowerToCommField();
		this.addEquipmentField();
		this.addAnchorSubform();
		this.addSpanField();
		this.addMakeReadyField();
	}

	private addStatusField() {
		const statusField = this.form.createField<FormModelFieldNestedList>(IkeFieldType.NESTED_LIST, {
			id: PoleFieldIds.STATUS,
			displayName: 'Status',
		});

		const builder = statusField.Mutator.builder;
		const optionGroup = builder.createGroup('Status');

		optionGroup.createValue('Field QC', undefined, '#024092');
		optionGroup.createValue('Field Recollect', undefined, '#f31233');
		optionGroup.createValue('HOA', undefined, '#66aee5');
		optionGroup.createValue('Field Recollect Precheck', undefined, '#b373e8');
		optionGroup.createValue('HOA QC', undefined, '#a3c4ef');
		optionGroup.createValue('HOA QC Rework', undefined, '#d92020');
		optionGroup.createValue('MRE', undefined, '#40f7f4');
		optionGroup.createValue('Final QC', undefined, '#21c4a9');
		optionGroup.createValue('Final QC Rework', undefined, '#ce542c');
		optionGroup.createValue('Delivery', undefined, '#0ff035');
		optionGroup.createValue('Billing', undefined, '#088c0a');
		optionGroup.createValue('Done', undefined, '#12800a');

		statusField.Mutator.init(builder.options);

		this.form.metadata.setStatusField(statusField);
		this.form.metadata.setMapColorField(statusField);
	}

	private addSequenceField() {
		this.form.createField(IkeFieldType.TEXT, {
			id: PoleFieldIds.SEQUENCE,
			displayName: 'Sequence',
		});
	}

	private addIdField() {
		this.form.createField(IkeFieldType.TEXT, {
			id: PoleFieldIds.POLE_ID,
			displayName: 'Id',
			title: true,
			locked: true,
		});
		this.form.Mutator.label([PoleFieldIds.POLE_ID]);
	}

	private addTagPhotosField() {
		this.form.createField(IkeFieldType.IMAGE, {
			id: PoleFieldIds.IMAGES,
			displayName: 'Tag Photos',
			locked: true,
		});
	}

	private addTypeField() {
		const typeField = this.form.createField<FormModelFieldNestedList>(IkeFieldType.NESTED_LIST, {
			id: PoleFieldIds.POLE_TYPE,
			cloneable: IkeCloneable.ALL,
			displayName: 'Type',
			locked: false,
		});

		const builder = typeField.Mutator.builder;
		const speciesGroup = builder.createGroup('Species');

		this.addSpeciesOptionWithClassAndHeights(speciesGroup, 'Wood', true);
		this.addSpeciesOptionWithClassAndHeights(speciesGroup, 'Steel', true);
		this.addSpeciesOptionWithClassAndHeights(speciesGroup, 'Fiberglass', false);
		this.addSpeciesOptionWithClassAndHeights(speciesGroup, 'Concrete', false);
		this.addSpeciesOptionWithClassAndHeights(speciesGroup, 'Other', true);

		typeField.Mutator.init(builder.options);
	}

	private addSpeciesOptionWithClassAndHeights(
		speciesGroup: FormModelFieldNestedListBuilderGroup,
		name: string,
		includeHPoleClasses: boolean
	) {
		const option = speciesGroup.createValue(name);
		this.addClassOptions(option, includeHPoleClasses ? [...normalClasses(), ...hClasses()] : normalClasses());
	}

	private addClassOptions(option: FormModelFieldNestedListBuilderValue, poleClasses: PoleClass[]) {
		const group = option.createGroup('Class of Pole');
		for (const poleClass of poleClasses) {
			const classOption = group.createValue(poleClass);
			this.addHeightOptions(classOption, poleClass);
		}
	}

	private addHeightOptions(value: FormModelFieldNestedListBuilderValue, poleClass: PoleClass) {
		const group = value.createGroup('Height');
		for (const height of poleLengthsForClass(poleClass)) {
			group.createValue(`${height}`);
		}
	}

	private addOwnerField(parent: FormModelFieldSubForm, id: string) {
		const ownerField = parent.createField<FormModelFieldNestedList>(IkeFieldType.NESTED_LIST, {
			id,
			cloneable: IkeCloneable.ALL,
			displayName: 'Owner',
			locked: false,
		});

		const builder = ownerField.Mutator.builder;
		const industryGroup = builder.createGroup('Industry');

		const utilityOption = industryGroup.createValue('UTILITY');
		const utilityOwnerIdGroup = utilityOption.createGroup('Owner Id');
		utilityOwnerIdGroup.createValue('Power');

		const communicationOption = industryGroup.createValue('COMMUNICATION');
		const communicationOwnerIdGroup = communicationOption.createGroup('Owner Id');
		communicationOwnerIdGroup.createValue('CATV');
		communicationOwnerIdGroup.createValue('Telco');
		communicationOwnerIdGroup.createValue('Fiber');
		communicationOwnerIdGroup.createValue('Unknown');

		ownerField.Mutator.init(builder.options);
	}

	private addCircumferenceField() {
		this.form.createField(IkeFieldType.NUMBER, {
			id: PoleFieldIds.CIRCUMFERENCE,
			displayName: 'Circumference (")',
			locked: true,
		});
	}

	private addTipField() {
		this.form.createField(IkeFieldType.HEIGHT, {
			id: PoleFieldIds.AGL,
			displayName: 'Tip',
			collector: IkeCollectorType.OFFICE,
			locked: true,
		});
	}

	private addLowPowerAttachmentField() {
		this.form.createField(IkeFieldType.HEIGHT, {
			id: PoleFieldIds.LOW_POWER_ATTACHMENT,
			displayName: 'Low Power Attachment',
			annotationLabelFieldId: PoleFieldIds.LOW_POWER_ATTACHMENT,
			collector: IkeCollectorType.OFFICE,
		});
	}

	private addLocationField() {
		this.form.createField(IkeFieldType.LOCATION, {
			id: PoleFieldIds.GEOGRAPHIC_COORDINATE,
			displayName: 'Location',
			locked: true,
		});
	}

	private addStreetAddressField() {
		const addressField = this.form.createField(IkeFieldType.TEXT, {
			id: PoleFieldIds.STREET_ADDRESS,
			displayName: 'Street Address',
			collector: IkeCollectorType.OFFICE,
		});

		this.form.metadata.setAddressLookupField(addressField);
	}

	private addNoteField(parent: FormModelFieldSubForm, id: string, displayName = 'Note') {
		parent.createField(IkeFieldType.TEXT, {
			id,
			displayName,
			locked: false,
		});
	}

	private addIkePhotoField() {
		this.form.createField(IkeFieldType.TRUE_SIZE, {
			id: PoleFieldIds.IKE_PHOTO,
			displayName: 'IKE Photo',
			collector: IkeCollectorType.ALL,
			required: true,
			locked: true,
		});
	}

	private addLowPowerToCommField() {
		this.form.createField(IkeFieldType.VERTICAL_SEPARATION, {
			id: PoleFieldIds.LOW_POWER_TO_COMM,
			displayName: 'Low Power to Comm',
			annotationLabelFieldId: PoleFieldIds.LOW_POWER_TO_COMM,
			...(this.hasVerticalSeparationToken && {
				clearanceThresholdInMeters: IkeUnit.round(IkeUnit.convert(40, IkeUnit.METER, IkeUnit.INCH), 3),
			}),
			collector: IkeCollectorType.OFFICE,
		});
	}

	private addEquipmentField() {
		const equipmentField = this.form.createField<FormModelFieldSubForm>(IkeFieldType.FORM, {
			id: PoleFieldIds.EQUIPMENT.FORM,
			displayName: 'Equipment',
			locked: true,
		});

		this.addEquipmentTypeField(equipmentField);
		this.addEquipmentOrientationField(equipmentField);
		this.addOwnerField(equipmentField, PoleFieldIds.EQUIPMENT.OWNER);
		this.addAttachmentHeightField(equipmentField, PoleFieldIds.EQUIPMENT.ATTACHMENT_HEIGHT, true);
		this.addEquipmentBottomHeightField(equipmentField);
		this.addNoteField(equipmentField, PoleFieldIds.EQUIPMENT.NOTE);

		equipmentField.Mutator.equipment.setFieldReferences(
			PoleFieldIds.EQUIPMENT.TYPE,
			PoleFieldIds.EQUIPMENT.ORIENTATION,
			PoleFieldIds.EQUIPMENT.OWNER,
			PoleFieldIds.EQUIPMENT.ATTACHMENT_HEIGHT,
			PoleFieldIds.EQUIPMENT.ATTACHMENT_BOTTOM_HEIGHT
		);
	}

	private addEquipmentTypeField(equipmentField: FormModelFieldSubForm) {
		const equipmentTypeField = equipmentField.createField<FormModelFieldNestedList>(IkeFieldType.NESTED_LIST, {
			id: PoleFieldIds.EQUIPMENT.TYPE,
			displayName: 'Type',
			title: true,
			locked: false,
		});

		const builder = equipmentTypeField.Mutator.builder;
		const typeGroup = builder.createGroup('Type');
		typeGroup.createValue('Capacitor Bank');
		typeGroup.createValue('Crossarm');
		typeGroup.createValue('Recloser');
		typeGroup.createValue('Riser');
		typeGroup.createValue('Streetlight');
		typeGroup.createValue('Transformer - Single Phase');
		typeGroup.createValue('Regulator');
		typeGroup.createValue('Transformer - Three Phase');
		typeGroup.createValue('Switch');

		equipmentTypeField.Mutator.init(builder.options);
	}

	private addEquipmentOrientationField(equipmentField: FormModelFieldSubForm) {
		equipmentField.createField(IkeFieldType.ORIENTATION, {
			id: PoleFieldIds.EQUIPMENT.ORIENTATION,
			displayName: 'Orientation',
			locked: true,
		});
	}

	private addEquipmentBottomHeightField(equipmentField: FormModelFieldSubForm) {
		equipmentField.createField(IkeFieldType.HEIGHT, {
			id: PoleFieldIds.EQUIPMENT.ATTACHMENT_BOTTOM_HEIGHT,
			displayName: 'Bottom Height',
			collector: IkeCollectorType.OFFICE,
			locked: true,
		});
	}

	private addAttachmentHeightField(
		parentField: FormModelFieldSubForm,
		id: string,
		locked: boolean,
		annotationLabelFieldId?: string
	) {
		parentField.createField(IkeFieldType.HEIGHT, {
			id,
			displayName: 'Attachment Height',
			collector: IkeCollectorType.OFFICE,
			locked,
			...(annotationLabelFieldId && { annotationLabelFieldId }),
		});
	}

	private addAnchorSubform() {
		const anchorField = this.form.createField<FormModelFieldSubForm>(IkeFieldType.FORM, {
			id: PoleFieldIds.ANCHOR.FORM,
			displayName: 'Anchor',
			locked: true,
		});

		this.addAnchorField(anchorField);
		this.addOwnerField(anchorField, PoleFieldIds.ANCHOR.OWNER);
		this.addNoteField(anchorField, PoleFieldIds.ANCHOR.NOTE);
		this.addAnchorGuyField(anchorField);

		anchorField.Mutator.anchor.setFieldReferences(PoleFieldIds.ANCHOR.LENGTH, PoleFieldIds.ANCHOR.OWNER);
	}

	private addAnchorField(anchorField: FormModelFieldSubForm) {
		anchorField.createField(IkeFieldType.ANCHOR, {
			id: PoleFieldIds.ANCHOR.LENGTH,
			displayName: 'Lead Length',
			locked: true,
		});
	}

	private addAnchorGuyField(anchorField: FormModelFieldSubForm) {
		const anchorGuyField = anchorField.createField<FormModelFieldSubForm>(IkeFieldType.FORM, {
			id: PoleFieldIds.ANCHOR.GUY.FORM,
			displayName: 'Guys',
			locked: true,
		});

		this.addAnchorGuySizeField(anchorGuyField);
		this.addOwnerField(anchorGuyField, PoleFieldIds.ANCHOR.GUY.OWNER);
		this.addAttachmentHeightField(anchorGuyField, PoleFieldIds.ANCHOR.GUY.ATTACHMENT_HEIGHT, true);
	}

	private addAnchorGuySizeField(anchorGuyField: FormModelFieldSubForm) {
		const anchorGuySizeField = anchorGuyField.createField<FormModelFieldNestedList>(IkeFieldType.NESTED_LIST, {
			id: PoleFieldIds.ANCHOR.GUY.SIZE,
			displayName: 'Size',
			title: true,
			locked: true,
		});

		const builder = anchorGuySizeField.Mutator.builder;
		const usageGroup = builder.createGroup('Usage Group');

		const downValue = usageGroup.createValue('Down');
		const downValueSizeGroup = downValue.createGroup('Size');

		downValueSizeGroup.createValue('EHS 1/8');
		downValueSizeGroup.createValue('EHS 5/32');
		downValueSizeGroup.createValue('EHS 3/16');
		downValueSizeGroup.createValue('EHS 7/32');
		downValueSizeGroup.createValue('EHS 1/4');
		downValueSizeGroup.createValue('EHS 9/32');
		downValueSizeGroup.createValue('EHS 5/16');
		downValueSizeGroup.createValue('EHS 3/8');
		downValueSizeGroup.createValue('EHS 7/16');
		downValueSizeGroup.createValue('EHS 1/2');
		downValueSizeGroup.createValue('EHS 9/16');
		downValueSizeGroup.createValue('EHS 5/8');
		downValueSizeGroup.createValue('6M');
		downValueSizeGroup.createValue('10M');
		downValueSizeGroup.createValue('12M');
		downValueSizeGroup.createValue('25M');

		anchorGuySizeField.Mutator.init(builder.options);
	}

	private addSpanField() {
		const spanField = this.form.createField<FormModelFieldSubForm>(IkeFieldType.FORM, {
			id: PoleFieldIds.SPAN.FORM,
			displayName: 'Span',
			cloneable: IkeCloneable.FIRST,
			locked: true,
		});

		this.addSpanLengthField(spanField);
		this.addSpanTypeField(spanField);
		this.addEnvironmentField(spanField);
		this.addMidSpanPhoto(spanField);
		this.addNoteField(spanField, PoleFieldIds.SPAN.NOTE);
		this.addSpanWireField(spanField);

		spanField.Mutator.span.setFieldReferences(
			PoleFieldIds.SPAN.LENGTH,
			PoleFieldIds.SPAN.TYPE,
			PoleFieldIds.SPAN.ENVIRONMENT,
			PoleFieldIds.SPAN.MID_SPAN_PHOTO
		);
	}

	private addSpanLengthField(spanField: FormModelFieldSubForm) {
		spanField.createField(IkeFieldType.VECTOR, {
			id: PoleFieldIds.SPAN.LENGTH,
			displayName: 'Span Length',
			locked: true,
		});
	}

	private addSpanTypeField(spanField: FormModelFieldSubForm) {
		const spanTypeField = spanField.createField<FormModelFieldSelectList>(IkeFieldType.SELECT_LIST, {
			id: PoleFieldIds.SPAN.TYPE,
			displayName: 'Type',
			title: true,
			locked: true,
		});

		spanTypeField.Mutator.option({ text: 'Fore Span', value: SpanType.NextPole });
		spanTypeField.Mutator.option({ text: 'Back Span', value: SpanType.PreviousPole });
		spanTypeField.Mutator.option({ text: 'Other', value: SpanType.Other });
		spanTypeField.Mutator.option({ text: 'Building', value: SpanType.Building });
	}

	private addEnvironmentField(spanField: FormModelFieldSubForm) {
		const environmentField = spanField.createField<FormModelFieldSelectList>(IkeFieldType.SELECT_LIST, {
			id: PoleFieldIds.SPAN.ENVIRONMENT,
			displayName: 'Environment',
			locked: false, // on purpose
		});

		for (const envItem of SPAN_ENVIRONMENT_VALUES) {
			environmentField.Mutator.option({ text: envItem, value: envItem });
		}
	}

	private addMidSpanPhoto(spanField: FormModelFieldSubForm) {
		spanField.createField(IkeFieldType.TRUE_SIZE, {
			id: PoleFieldIds.SPAN.MID_SPAN_PHOTO,
			displayName: 'Mid Span IKE Photo',
			locked: true,
		});
	}

	private addSpanWireField(spanField: FormModelFieldSubForm) {
		const wireField = spanField.createField<FormModelFieldSubForm>(IkeFieldType.FORM, {
			id: PoleFieldIds.SPAN.WIRE.FORM,
			displayName: 'Wire',
			cloneable: IkeCloneable.FIRST,
			locked: true,
		});

		this.addWireMidSpanHeightField(wireField);
		this.addWireUsageGroupField(wireField);
		this.addOwnerField(wireField, PoleFieldIds.SPAN.WIRE.OWNER);
		this.addAttachmentHeightField(wireField, PoleFieldIds.SPAN.WIRE.ATTACHMENT_HEIGHT, true);
	}

	private addWireMidSpanHeightField(wireField: FormModelFieldSubForm) {
		wireField.createField(IkeFieldType.HEIGHT, {
			id: PoleFieldIds.SPAN.WIRE.MID_SPAN_HEIGHT,
			displayName: 'Mid Span Height',
			locked: true,
		});
	}

	private addWireUsageGroupField(wireField: FormModelFieldSubForm) {
		const wireUsageGroupField = wireField.createField<FormModelFieldSelectList>(IkeFieldType.SELECT_LIST, {
			id: PoleFieldIds.SPAN.WIRE.USAGE_GROUP,
			displayName: 'Usage Group',
		});

		wireUsageGroupField.Mutator.option({ text: 'Primary', value: 'PRIMARY' });
		wireUsageGroupField.Mutator.option({ text: 'Neutral', value: 'NEUTRAL' });
		wireUsageGroupField.Mutator.option({ text: 'Secondary', value: 'SECONDARY' });
		wireUsageGroupField.Mutator.option({ text: 'Comm Messenger', value: 'COMM_MESSENGER' });
		wireUsageGroupField.Mutator.option({ text: 'CATV', value: 'CATV' });
		wireUsageGroupField.Mutator.option({ text: 'Telco', value: 'TELCO' });
		wireUsageGroupField.Mutator.option({ text: 'Fiber', value: 'FIBER' });
		wireUsageGroupField.Mutator.option({ text: 'Span Guy', value: 'SPAN_GUY' });
	}

	private addMakeReadyField() {
		const makeReadyField = this.form.createField<FormModelFieldSubForm>(IkeFieldType.FORM, {
			id: PoleFieldIds.MAKE_READY.FORM,
			displayName: 'Make Ready',
			collector: IkeCollectorType.OFFICE,
		});

		this.addMakeReadyAttachmentSizeField(makeReadyField);
		this.addMakeReadyAttachmentTypeField(makeReadyField);
		this.addAttachmentHeightField(
			makeReadyField,
			PoleFieldIds.MAKE_READY.ATTACHMENT_HEIGHT,
			false,
			PoleFieldIds.MAKE_READY.ATTACHMENT_SIZE
		);
		this.addMakeReadyProposedMidSpanField(makeReadyField);
		this.addMakeReadyProposedRiserField(makeReadyField);
		this.addMakeReadyTypeField(makeReadyField);
		this.addNoteField(makeReadyField, PoleFieldIds.MAKE_READY.NOTE, 'Make Ready Notes');
	}

	private addMakeReadyAttachmentSizeField(makeReadyField: FormModelFieldSubForm) {
		makeReadyField.createField(IkeFieldType.TEXT, {
			id: PoleFieldIds.MAKE_READY.ATTACHMENT_SIZE,
			displayName: 'Attachment Size',
			title: true,
		});
	}

	private addMakeReadyAttachmentTypeField(makeReadyField: FormModelFieldSubForm) {
		const makeReadyAttachmentTypeField = makeReadyField.createField<FormModelFieldSelectList>(
			IkeFieldType.SELECT_LIST,
			{
				id: PoleFieldIds.MAKE_READY.ATTACHMENT_TYPE,
				displayName: 'Attachment Type',
			}
		);

		makeReadyAttachmentTypeField.Mutator.option({ text: 'New', value: 'NEW' });
		makeReadyAttachmentTypeField.Mutator.option({ text: 'Overlash', value: 'OVERLASH' });
	}

	private addMakeReadyProposedMidSpanField(makeReadyField: FormModelFieldSubForm) {
		makeReadyField.createField(IkeFieldType.HEIGHT, {
			id: PoleFieldIds.MAKE_READY.PROPOSED_MID_SPAN,
			displayName: 'Proposed Mid Span',
		});
	}

	private addMakeReadyProposedRiserField(makeReadyField: FormModelFieldSubForm) {
		const makeReadyProposedRiserField = makeReadyField.createField<FormModelFieldSelectList>(IkeFieldType.SELECT_LIST, {
			id: PoleFieldIds.MAKE_READY.PROPOSED_RISER,
			displayName: 'Proposed Riser',
			collector: IkeCollectorType.OFFICE,
		});

		makeReadyProposedRiserField.Mutator.option({ text: 'N', value: 'N' });
		makeReadyProposedRiserField.Mutator.option({ text: 'E', value: 'E' });
		makeReadyProposedRiserField.Mutator.option({ text: 'S', value: 'S' });
		makeReadyProposedRiserField.Mutator.option({ text: 'W', value: 'W' });
		makeReadyProposedRiserField.Mutator.option({ text: 'NE', value: 'NE' });
		makeReadyProposedRiserField.Mutator.option({ text: 'NW', value: 'NW' });
		makeReadyProposedRiserField.Mutator.option({ text: 'SE', value: 'SE' });
		makeReadyProposedRiserField.Mutator.option({ text: 'SW', value: 'SW' });
	}

	private addMakeReadyTypeField(makeReadyField: FormModelFieldSubForm) {
		const makeReadyTypeField = makeReadyField.createField<FormModelFieldSelectList>(IkeFieldType.SELECT_LIST, {
			id: PoleFieldIds.MAKE_READY.TYPE,
			displayName: 'Type',
		});

		makeReadyTypeField.Mutator.option({ text: 'Power', value: 'POWER' });
		makeReadyTypeField.Mutator.option({ text: 'Comm', value: 'COMM' });
		makeReadyTypeField.Mutator.option({ text: 'Other', value: 'OTHER' });
	}
}
